import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useGetDeviceListingQuery } from "../../../api/network/camerasAdminVMApiService";
import {
  useGetCameraByCidQuery,
  useIncomingVoicecallAnswerMutation,
  useIncomingVoicecallDeclineMutation,
} from "../../../api/network/vmApiService";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import {
  VMDashNotifyResponseIncomingCall,
  VMIncomingCallStartParam,
} from "../../../api/types/CamerasVisionMax";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../../api/types/Onboarding";
import { TLegacyUserWithAccount } from "../../../api/types/User";
import svgIcons from "../../../assets/svg/svgIcons";
import { APP_CONFIG } from "../../../constants/constants";
import { asyncLoadScript } from "../../../utils/common";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import VoicecallNotificationAudio from "./voicecallNotificationAudio";

type ModalVMCameraVoicecallIncomingProps = {
  incomingCall: VMDashNotifyResponseIncomingCall;
  onHideCallback?: () => void;
};

const ModalVMCameraVoicecallIncoming: FC<
  ModalVMCameraVoicecallIncomingProps
> = ({ incomingCall, onHideCallback }) => {
  const {
    notify_list: { voip: voipData },
  } = incomingCall;
  const { t } = useTranslation();

  const initRef = React.useRef(false);
  const [progressValue, setProgressValue] = React.useState<number>(0);
  const [voicecallError, setVoicecallError] = React.useState<string | null>(
    null
  );

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const dispatch = useDispatch();

  const { getLocalStorageItem } = useLocalStorage();
  const loggedInUserData: LegacyVerifyEmailOtpResponseSuccess = useSelector(
    (state: any) => state.commonReducer.user
  );
  const loggedInUser: TLegacyUserWithAccount =
    loggedInUserData?.user ?? getLocalStorageItem(localStorageKeys.user);

  //  Get CID from voip data and fetch camera details to get serial number
  const incomingCallCID = voipData?.credential?.cid;

  // Step 1: Fetch serialNumber using incomingCallCID
  const { data: cameraDetailsResponse } = useGetCameraByCidQuery(
    incomingCallCID,
    {
      skip: !incomingCallCID,
      refetchOnMountOrArgChange: true,
    }
  );
  const serialNumber = cameraDetailsResponse?.data?.[0]?.serial_number ?? "";

  // Fetch device data using serialNumber
  const { data: deviceListingResponse, isFetching: isFetchingDeviceListing } =
    useGetDeviceListingQuery(
      {
        keyword: serialNumber,
        model: "",
        status: "",
        offset: 0,
        limit: 1,
      },
      {
        skip: !serialNumber,
        refetchOnMountOrArgChange: true,
      }
    );
  const deviceData = deviceListingResponse?.data?.[0] ?? null;

  // Extract assetId and driver name
  const assetId = deviceData?.device_id || "Not Available";
  const driverName = deviceData?.drivername || "Not Available";

  const webRTCConfig = {
    // "staging" | "prod"
    env: loggedInUser?.config?.VISIONMAX_BASE_URL?.includes("staging")
      ? "staging"
      : "prod",
    // VM_ENV === "prod"
    //   ? process.env.REACT_APP_VISIONMAX_ENV
    //   : process.env.REACT_APP_VISIONMAX_ENV_STAGING,
    apiKey: loggedInUser?.config?.VISIONMAX_X_API_KEY,
    // VM_ENV === "prod"
    //   ? process.env.REACT_APP_VISIONMAX_KEY
    //   : process.env.REACT_APP_VISIONMAX_KEY_STAGING,
    clientAccessToken: localStorage.getItem(
      localStorageKeys.camerasAdminVMToken
    ),
    feature: {
      enableDefaultCameraSwitchButton: false,
    },
  };

  // by default, when the voice call starts, the call is unmuted
  const [voicecallMuted, setVoicecallMuted] = React.useState<boolean>(false);
  const muteUnmuteVoicecall = (mute = true) => {
    if (APP_CONFIG.DEBUG.ALL) console.log("[muteUnmuteVoicecall]", mute);
    const vmRTC = (window as any).vmRTC;
    if (vmRTC) {
      if (mute) {
        vmRTC.disableLocalStreamSender("audio");
        setVoicecallMuted(true);
      } else {
        vmRTC.enableLocalStreamSender("audio");
        setVoicecallMuted(false);
      }
    } else {
      if (APP_CONFIG.DEBUG.ALL)
        console.error("== [muteUnmuteVoicecall] vmRTC is not defined");
    }
  };

  // flag to indicate if the microphone permission was denied
  const [microphonePermissionDenied, setMicrophonePermissionDenied] =
    React.useState<boolean>(false);

  // flag to indicate if the incoming voice call action (answer / decline) is taken
  const [voicecallActionTaken, setVoicecallActionTaken] = React.useState<
    "YES" | "NO" | "PROCESSING"
  >("NO");

  // flag to indicate if the voice call is picked up at the device
  const [voicecallPicked, setVoicecallPicked] = React.useState<boolean>(false);

  // event handlers
  const vmRTCEventHandlerEventMapping = {
    progressStatusUpdated: "progressStatusUpdated",
    peer_connected: "peer connected",
    session_stable: "session stable",
    fail_to_get_system_input_devices: "fail to get system input devices",
    requiring_the_permission_of_microphone:
      "requiring the permission of microphone",
    fail_to_require_the_permission_of_microphone:
      "fail to require the permission of microphone",
    required_the_permission_of_microphone:
      "required the permission of microphone",
    call_picked_at_the_device: "call picked at the device",
    not_answered_by_the_device: "not answered by the device",
    camera_not_supported_or_feature_disabled:
      "camera not supported or feature disabled",
    connect_fail: "connect fail",
    connection_lost: "connection lost",
    initial_fail: "initial.fail",
  };
  const vmRTCEventHandlers = {
    progressStatusUpdated: (input: any) => {
      // setProgressValue(parseInt(input, 10));
      console_log("== WEBRTCEVENT [progressStatusUpdated]", input);
      setProgressValue(input);
    },
    peer_connected: () => {
      console_log("== WEBRTCEVENT [peer connected]");
    },
    session_stable: () => {
      const vmRTC = (window as any).vmRTC;
      console_log("== WEBRTCEVENT [session stable]");
      // vmRTC.disableLocalStreamSender("audio");
      // vmRTC.disableRemoteStreamReceiver("audio");
      // setVoicecallMuted(true);
      vmRTC.enableLocalStreamSender("audio");
      vmRTC.enableRemoteStreamReceiver("audio");
      setVoicecallMuted(false);
    },
    fail_to_get_system_input_devices: () => {
      console_log("== WEBRTCEVENT [fail to get system input devices]");
    },
    requiring_the_permission_of_microphone: () => {
      console_log("== WEBRTCEVENT [requiring the permission of microphone]");
    },
    fail_to_require_the_permission_of_microphone: () => {
      console_log(
        "== WEBRTCEVENT [fail to require the permission of microphone]"
      );
      setMicrophonePermissionDenied(true);
      setVoicecallError("The permission to use microphone was denied");
    },
    required_the_permission_of_microphone: () => {
      console_log("== WEBRTCEVENT [required the permission of microphone]");
    },
    call_picked_at_the_device: () => {
      const vmRTC = (window as any).vmRTC;
      console_log("== WEBRTCEVENT [call picked at the device]");
      setVoicecallPicked(true);
      // vmRTC.enableLocalStreamSender("audio");
      // vmRTC.enableRemoteStreamReceiver("audio");
      // setVoicecallMuted(false);
    },
    not_answered_by_the_device: () => {
      const vmRTC = (window as any).vmRTC;
      vmRTC.stop();
      console_log("== WEBRTCEVENT [not answered by the device]");
      setVoicecallError("The call was not answered by the device");
    },
    camera_not_supported_or_feature_disabled: () => {
      const vmRTC = (window as any).vmRTC;
      vmRTC.stop();
      console_log("== WEBRTCEVENT [camera not supported or feature disabled]");
      setVoicecallError("camera not supported or feature disabled");
    },
    connect_fail: (input: any) => {
      const vmRTC = (window as any).vmRTC;
      vmRTC.stop();
      console_log("== WEBRTCEVENT [connect fail]", input);
      setVoicecallError(
        "Connect failed." +
          (microphonePermissionDenied
            ? " Please check your microphone permission."
            : "")
      );
    },
    connection_lost: () => {
      const vmRTC = (window as any).vmRTC;
      vmRTC.stop();
      console_log("== WEBRTCEVENT [connection lost]");
      setVoicecallError("connection lost");
    },
    initial_fail: (input: any) => {
      console_log("== WEBRTCEVENT [initial.fail]", input);
      setVoicecallError("initial fail");
    },
  };

  const initializeVoicecallSession = React.useCallback(() => {
    if (voicecallPicked) return; // added to prevent the vmRTC instance to reinitialize if voipData changes
    const vmRTC = (window as any).vmRTC;
    const incomingCallStartParam: VMIncomingCallStartParam = {
      voip: voipData,
      request_id: voipData?.credential?.request_id,
      callDirection: "incoming",
    };
    vmRTC
      .setup(webRTCConfig, "audio_call")
      .startIncomingCallSession(
        voipData.credential.cid,
        "audio_call",
        incomingCallStartParam
      );
  }, [voipData, voicecallPicked]);

  const initVoicecall = React.useCallback(() => {
    if (initRef.current) return;

    // update the ref to ensure the code executes only once
    initRef.current = true;

    // initialize scripts start here
    console_log("== [initVoicecall]");

    // import the webRTC script from `public/js` directory
    // ---------------------------------------------------
    // other dependencies (aws-sdk-2.1490.0.min.js, mqttws31.js and adapter.js)
    // are already loaded in the index.html file
    // a copy of each of these files is archived in `src/components/visionmax-liveview-ui/archive/` directory
    asyncLoadScript(
      `${process.env.PUBLIC_URL}/js/vmRTC-umd.js`,
      "visionmax-liveview-webrtc",
      () => {
        console_log("== [initVoicecall] webrtc script loaded");
        try {
          // initialize the webRTC
          const vmRTC = (window as any).vmRTC;
          if (vmRTC) {
            // bind event handlers
            for (const eventKey in vmRTCEventHandlerEventMapping) {
              vmRTC.addEventListener(
                vmRTCEventHandlerEventMapping?.[
                  eventKey as keyof typeof vmRTCEventHandlerEventMapping
                ],
                (vmRTCEventHandlers as any)[
                  eventKey
                ] as keyof typeof vmRTCEventHandlers
              );
            }

            // setup and start
            console_log("== [INCOMING CID]", voipData?.credential?.cid);
            // skipping initialization here as here action buttons (answer / decline) should be displayed
            // const incomingCallStartParam: VMIncomingCallStartParam = {
            //   voip: voipData,
            //   request_id: voipData?.credential?.request_id,
            //   callDirection: "incoming",
            // };
            // vmRTC
            //   .setup(webRTCConfig, "audio_call")
            //   .startIncomingCallSession(
            //     voipData.credential.cid,
            //     "audio_call",
            //     incomingCallStartParam
            //   );
            // try initializing here OR move it to success of API request for answering the call
            initializeVoicecallSession();
          } else {
            console.error("I == [initVoicecall] vmRTC is not defined");
          }
        } catch (e) {
          if (APP_CONFIG.DEBUG.ALL) console.error("I == ERR", e);
        }
      }
    );
  }, [microphonePermissionDenied, voipData]);

  // initialize voice call
  useEffect(() => {
    initVoicecall();
  }, []);

  // remove event listeners on unmount
  useEffect(() => {
    return () => {
      console_log("== [ModalVMCameraVoicecallIncoming] unmounting");
      const vmRTC = (window as any).vmRTC;
      if (vmRTC) {
        // remove event listeners
        for (const eventKey in vmRTCEventHandlerEventMapping) {
          vmRTC.removeEventListener(
            vmRTCEventHandlerEventMapping?.[
              eventKey as keyof typeof vmRTCEventHandlerEventMapping
            ],
            (vmRTCEventHandlers as any)[
              eventKey
            ] as keyof typeof vmRTCEventHandlers
          );
        }
        // stop the session
        vmRTC.stop();
      }
    };
  }, []);

  const [
    incomingVoicecallAnswerAPITrigger,
    { isLoading: isLoadingIncomingVoicecallAnswer },
  ] = useIncomingVoicecallAnswerMutation();
  const [
    incomingVoicecallDeclineAPITrigger,
    { isLoading: isLoadingIncomingVoicecallDecline },
  ] = useIncomingVoicecallDeclineMutation();

  // request to answer the call
  const pickIncomingCall = () => {
    console_log("== [pickIncomingCall]");

    // set the action taken
    setVoicecallActionTaken("PROCESSING");

    // call the API to answer the incoming voice call
    incomingVoicecallAnswerAPITrigger(voipData?.credential?.request_id)
      .unwrap()
      .then((response) => {
        console_log("== [pickIncomingCall] API response", response);
        // interpret the response
        let msg = "ok";
        const { code } = response.data;

        switch (code) {
          case "E31001":
            msg = "Someone else has already answered the call";
            break;
          case "E31003":
            msg = "The call is finished";
            break;
          case "E31004":
            msg = "You can not take the same voice call again";
            break;
        }

        if (msg === "ok") {
          console_log("== [pickIncomingCall] Answering the call now");

          // unmute to start the session
          setVoicecallPicked(true);
          const vmRTC = (window as any).vmRTC;
          // vmRTC.enableLocalStreamSender("audio");
          // vmRTC.enableRemoteStreamReceiver("audio");
          // setVoicecallMuted(false);
        } else {
          console_log(
            `== [pickIncomingCall] Could not answere the call - ${msg}`
          );
          setVoicecallError(msg);
        }
      })
      .catch((error) => {
        console.error("I == [pickIncomingCall] API error", error);
        setVoicecallError(error?.data?.message || "Failed to answer the call");
      })
      .finally(() => {
        // reset the action taken
        setVoicecallActionTaken("YES");
      });
  };

  // a request will be sent to notify the API server that it no longer needs to
  // send information related to this call.
  const declineIncomingCall = () => {
    console_log("== [declineIncomingCall]");

    // set the action taken
    setVoicecallActionTaken("PROCESSING");

    // call the API to answer the incoming voice call
    incomingVoicecallDeclineAPITrigger(voipData?.credential?.request_id)
      .unwrap()
      .then((response) => {
        console_log("== [declineIncomingCall] API response", response);
      })
      .catch((error) => {
        console.error("I == [declineIncomingCall] API error", error);
        setVoicecallError(error?.data?.message || "Failed to decline the call");
      })
      .finally(() => {
        // reset the action taken
        setVoicecallActionTaken("YES");
        // reset and hide the modal
        hideModal();
      });
  };

  const hideModal = () => {
    const vmRTC = (window as any).vmRTC;
    if (vmRTC) {
      vmRTC.stop();
    }
    onHideCallback?.();
    dispatch(
      setModalsData({
        ...modalsState,
        showModalVMCameraVoicecallIncoming: null,
      })
    );
  };

  const AudioElement = React.useCallback(() => {
    return (
      <audio
        className={`absolute opacity-0 peer-[]:opacity-100 top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-6rem)] z-[1]`}
        controls
        id="rtc_audio"
        autoPlay
      ></audio>
    );
  }, []);

  const [hangUp, setHangUp] = useState(false);
  const handleHangUp = () => {
    setHangUp(true);
    setTimeout(() => {
      hideModal();
    }, 500);
  };

  if (modalsState.showModalVMCameraVoicecallIncoming === null) return null;

  return (
    <>
      {(!voicecallPicked || voicecallError) && (
        <div
          className="fixed w-full h-screen top-0 left-0 bg-modal-overlay z-vmvoicecall-overlay"
          // non-dismissable
          // onClick={hideModal}
        />
      )}
      <div
        id="visionmax-liveview"
        className={
          `fixed transition-all duration-500 ${voicecallPicked && !voicecallError ? "top-[calc(100vh-8rem)]" : "top-1/2"} inset-x-1/2 w-[560px] max-w-full h-48 bg-black/80 Xoverflow-hidden ` +
          "Xbg-white rounded-xl shadow-lg z-vmvoicecall"
        }
        style={{ transform: "translate(-50%, -50%)" }}
      >
        {/* Debug */}
        {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
          <pre className="absolute bottom-full left-0 bg-white/50 p-2 text-[8px]">
            progressValue: {progressValue}
            <br />
            voicecallError: {JSON.stringify(voicecallError)}
            <br />
            voicecallMuted: {JSON.stringify(voicecallMuted)}
            <br />
            microphonePermissionDenied:{" "}
            {JSON.stringify(microphonePermissionDenied)}
            <br />
            voicecallActionTaken: {JSON.stringify(voicecallActionTaken)}
            <br />
            voicecallPicked: {JSON.stringify(voicecallPicked)}
            <br />
            CID: {incomingCallCID}
            <br />
            Serial Number: {serialNumber}
            <br />
            Asset ID: {assetId}
            <br />
            Driver Name: {driverName}
          </pre>
        )}
        {/* Camera Buttons Area */}
        <div
          className={`absolute${voicecallActionTaken === "YES" ? "" : voicecallError ? "" : " hidden"} ${voicecallError || progressValue < 100 ? "top-4 right-4" : "top-2/3 left-1/2 -translate-x-1/2 -translate-y-1/2 mt-2"} flex flex-row gap-2 z-50 p-2 bg-white/20 rounded-full`}
        >
          {!voicecallError && progressValue === 100 && (
            <>
              {/* Status indicator */}
              <span
                className={`size-4 m-3 rounded-full ${voicecallPicked ? "bg-green-500" : "bg-amber-500/50"}`}
              />
              {/* unmute / mute buttons */}
              <button
                key={`btn_unmute_${voipData.credential.cid}`}
                className={
                  "h-10 flex items-center justify-center rounded-full enabled:focus:ring enabled:focus:ring-white outline-0 " +
                  "enabled:bg-white/50 enabled:hover:bg-white disabled:bg-black/25 disabled:text-white/25 " +
                  "px-4 uppercase font-bold"
                }
                onClick={() => {
                  muteUnmuteVoicecall(false);
                }}
                disabled={
                  !progressValue ||
                  progressValue < 100 ||
                  !voicecallPicked ||
                  !voicecallMuted
                }
              >
                {t("unmute")}
              </button>
              <button
                key={`btn_mute_${voipData.credential.cid}`}
                className={
                  "h-10 flex items-center justify-center rounded-full enabled:focus:ring enabled:focus:ring-white outline-0 " +
                  "enabled:bg-white/50 enabled:hover:bg-white disabled:bg-black/25 disabled:text-white/25 " +
                  "px-4 uppercase font-bold"
                }
                onClick={() => {
                  muteUnmuteVoicecall(true);
                }}
                disabled={
                  !progressValue ||
                  progressValue < 100 ||
                  !voicecallPicked ||
                  voicecallMuted
                }
              >
                {t("mute")}
              </button>

              {/* separator */}
              <div className="w-px h-10 bg-white/15" />
            </>
          )}

          {/* Close / Hang Up Button */}
          {true && ( // (progressValue === 100 || voicecallError)
            <button
              className={
                `group flex items-center justify-center rounded-full focus:ring focus:ring-white uppercase font-bold ` +
                `${
                  progressValue === 100 && voicecallPicked && !voicecallError
                    ? "h-10 px-4 bg-field-error-dark text-white hover:bg-field-error-dark/75 focus:bg-field-error-dark/90"
                    : "size-10 bg-black hover:bg-gray-100"
                } outline-0 whitespace-nowrap`
              }
              onClick={handleHangUp}
            >
              {progressValue === 100 && voicecallPicked && !voicecallError ? (
                <>{t("hangup")}</>
              ) : (
                <img
                  src={svgIcons.CloseIcon}
                  alt={
                    progressValue === 100 && voicecallPicked
                      ? "Hangup"
                      : "Close"
                  }
                  className="grayscale-0 brightness-0 invert group-hover:invert-0 opacity-50"
                />
              )}
            </button>
          )}
        </div>

        {/* Progress bar or error message display */}
        {!!voicecallError ? (
          <p className="absolute z-[2] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-lg uppercase font-bold text-gray-200/70 text-center">
            {voicecallError}
          </p>
        ) : voicecallActionTaken === "YES" && progressValue < 100 ? (
          <div className="absolute z-[2] top-0 left-0 w-full h-full flex items-center justify-center">
            <div
              className="w-[calc(100%-6rem)] border border-gray-400/70 rounded-md h-12 relative translate-y-6"
              role="progressbar"
              aria-label={`loading progress`}
              aria-valuemin={0}
              aria-valuenow={progressValue}
              aria-valuemax={100}
            >
              <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -mt-16 text-lg uppercase font-bold text-gray-200/70 text-center">
                {t("connecting")}...
              </span>
              <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-gray-200/70">
                {progressValue}%
              </span>
              <div
                className="absolute top-0 left-0 h-full bg-gray-400/25"
                style={{ width: `${progressValue}%` }}
              ></div>
            </div>
          </div>
        ) : voicecallPicked ? null : (
          <div className="absolute z-[2] top-0 left-0 w-full h-full flex flex-col items-center justify-center">
            <span className="text-lg uppercase font-bold text-gray-200/50">
              {t("incomingcall")}...
            </span>
            {/* Asset Info Display - on incoming call popup */}
            {!!deviceData &&
              !isFetchingDeviceListing &&
              (!!assetId || !!driverName) && (
                <div className="Xabsolute Xtop-2 Xleft-2 Xbg-black/70 flex gap-4 font-bold text-lg text-gray-200 mt-2">
                  <span>Asset ID: {assetId || "Not Available"}</span>
                  <span>Driver: {driverName || "Not Available"}</span>
                </div>
              )}
            <div
              className={`flex gap-4 ${!!assetId || !!driverName ? "mt-4" : "mt-6"}`}
            >
              <button
                className="h-10 px-4 bg-field-success text-white rounded-full hover:bg-field-success/75 focus:bg-field-success/90 uppercase font-bold"
                onClick={pickIncomingCall}
                disabled={
                  isLoadingIncomingVoicecallAnswer ||
                  isLoadingIncomingVoicecallDecline ||
                  voicecallActionTaken === "PROCESSING"
                }
              >
                {t("answer")}
              </button>
              <button
                className="h-10 px-4 bg-field-error-dark text-white rounded-full hover:bg-field-error-dark/75 focus:bg-field-error-dark/90 uppercase font-bold"
                onClick={declineIncomingCall}
                disabled={
                  isLoadingIncomingVoicecallAnswer ||
                  isLoadingIncomingVoicecallDecline ||
                  voicecallActionTaken === "PROCESSING"
                }
              >
                {t("decline")}
              </button>
            </div>
          </div>
        )}

        {/* Asset Info Display - after call is picked */}
        {!voicecallError &&
          progressValue === 100 &&
          !!voicecallPicked &&
          !hangUp &&
          !!deviceData &&
          !isFetchingDeviceListing &&
          (!!assetId || !!driverName) && (
            <div className="absolute top-4 left-4 Xbg-black/70 flex flex-col text-xs font-bold text-gray-200">
              <span>Asset ID: {assetId || "Not Available"}</span>
              <span>Driver: {driverName || "Not Available"}</span>
            </div>
          )}

        {/* Video element */}
        {progressValue === 100 && voicecallPicked && (
          <span className={`peer`}></span> // when this DOM element renders, the audio element is displayed
        )}
        {!voicecallError && AudioElement()}
        {/* <AudioElement /> */}

        {/* Incoming voicecall audio */}
        {!voicecallPicked && <VoicecallNotificationAudio type="ringing" />}
        {!!hangUp && <VoicecallNotificationAudio type="hangup" />}
      </div>
    </>
  );
};

function console_log(...params: any[]) {
  if (APP_CONFIG.DEBUG.ALL) {
    console.log(`I ${params[0]}`, ...params.slice(1));
  }
}

export default ModalVMCameraVoicecallIncoming;
